<script setup>
import { computed, defineProps } from "vue";
import { useChatBot } from "@/components/ChatBot/composables/useChatBot";
import { useChatbotUI } from "@/components/ChatBot/composables/useChatBotUI";
import i18n from "@/core/plugins/vue-i18n";
import { getSubdomain } from "@/core/services/api.service";
import { useStore } from "@/core/services/store";
import { capitalize } from "lodash";

const props = defineProps({
  errorMessage: {
    type: String,
    required: true
  }
});

const { sendMessage } = useChatBot();
const { toggleChatbot, isOpen } = useChatbotUI();
const store = useStore();

/**
 * Check if chatbot is enabled for the current subdomain
 * Can be removed if chatbot is enabled for all users
 */
const showButton = computed(() => {
  const subdomain = getSubdomain(window.location.origin);
  const licenseIds = process.env.VUE_APP_HELPBOT_LICENSE_IDS
    ? process.env.VUE_APP_HELPBOT_LICENSE_IDS.split(",").map(item =>
        item.trim()
      )
    : [];

  return licenseIds.includes(subdomain);
});

/**
 * Validate that an error message is a non-empty string
 */
const isValidError = computed(() => {
  return (
    props.errorMessage &&
    typeof props.errorMessage === "string" &&
    props.errorMessage.trim().length > 0
  );
});

const scmaName = computed(() => {
  return capitalize(store.getters.selectedApp.name);
});

/**
 * Handle the explain error button click
 * Opens the chatbot and sends a pre-formatted message requesting explanation
 */
const handleExplainError = async () => {
  if (!isValidError.value) {
    return;
  }

  // Open the chatbot if it's not already open
  if (!isOpen.value) {
    toggleChatbot();
  }

  // Format the error message with proper escaping
  const sanitizedError = props.errorMessage.trim();
  const message = i18n.t("chatBot.explainErrorTemplate", {
    appName: scmaName.value,
    error: sanitizedError
  });

  // Send the message to the chatbot
  await store.dispatch("setAbortController", {
    abortController: new AbortController()
  });
  await sendMessage(message);
  await store.dispatch("setAbortController", { abortController: null });
};
</script>

<template>
  <i
    v-if="showButton && isValidError"
    v-b-tooltip="$t('chatBot.explainErrorButtonLabel')"
    class="fa-kit fa-light-lightbulb-sparkle ml-2"
    @click="handleExplainError"
  />
</template>

<style scoped lang="scss">
$chatbot-primary-gradient-color: #3f46f2;
$chatbot-secondary-gradient-color: #c750c9;

.fa-light-lightbulb-sparkle {
  background-clip: text;
  color: transparent;
  background: linear-gradient(
    135deg,
    $chatbot-primary-gradient-color,
    $chatbot-secondary-gradient-color
  );
  -webkit-background-clip: text;
  background-clip: text;
  transition: color 0.2s ease-in-out;
}
</style>
